// src/components/CashPayment.tsx
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatApproxEur } from '../utils/currency';
import { Reservation } from '../types';

const CashPayment: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const state = location.state as { reservation: Reservation } | null;
  const [reservation, setReservation] = useState<Reservation | null>(state?.reservation ?? null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (state?.reservation) {
      setReservation(state.reservation);
      localStorage.setItem('reservationData', JSON.stringify(state.reservation));
      return;
    }
    const raw = localStorage.getItem('reservationData');
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as Reservation;
        setReservation(parsed);
        return;
      } catch {
        // fall through
      }
    }
    setError(t('cash.notFound'));
  }, [state, t]);

  useEffect(() => {
    if (reservation && !reservation.isVerified) {
      void navigate('/verify', { state: { reservation, paymentMethod: reservation.paymentMethod ?? null } });
    }
  }, [reservation, navigate]);

  if (!reservation) {
    return (
      <div className="max-w-xl mx-auto mt-10 p-6 bg-white rounded shadow space-y-4">
        <p className="text-red-500 text-center">{error || t('cash.notFound')}</p>
        <button
          className="w-full bg-blue-600 text-white py-3 rounded hover:bg-blue-700 transition"
          onClick={() => void navigate('/rezervace')}
        >
          {t('cash.backToReservation')}
        </button>
      </div>
    );
  }

  // Build datetimes
  const inDt = new Date(reservation.checkInDate);
  const [hIn, mIn] = reservation.checkInTime.split(':').map(Number);
  inDt.setHours(hIn, mIn, 0, 0);

  const outDt = new Date(reservation.checkOutDate);
  const [hOut, mOut] = reservation.checkOutTime.split(':').map(Number);
  outDt.setHours(hOut, mOut, 0, 0);

  const msPerDay = 1000 * 60 * 60 * 24;
  const nights = Math.ceil((outDt.getTime() - inDt.getTime()) / msPerDay);

  return (
    <div className="max-w-xl mx-auto mt-10 p-6 bg-white rounded shadow space-y-4">
      <h2 className="text-2xl font-bold text-center text-[#4B2E2E]">{t('cash.thanksTitle')}</h2>
      <p className="text-lg text-center text-gray-700">
        {t('cash.thanksText')}
      </p>

      {/* Shrnutí rezervace */}
      <div className="space-y-2">
        <div><strong>{t('payment.reservationNumber')}:</strong> {reservation.id}</div>
        <div><strong>{t('payment.stayTerm')}:</strong> {inDt.toLocaleDateString()} – {outDt.toLocaleDateString()}</div>
        <div><strong>{t('payment.nights')}:</strong> {nights}</div>
        <div><strong>{t('payment.persons')}:</strong> {reservation.adults} {t('payment.adults')}, {reservation.children} {t('payment.children')}</div>
        <div>
          <strong>{t('payment.totalPrice')}:</strong> {reservation.totalPrice?.toLocaleString()} Kč
          {i18n.language !== 'cs' && reservation.totalPrice && (
            <div className="text-sm text-gray-600 mt-1">(~ {formatApproxEur(reservation.totalPrice, i18n.language)})</div>
          )}
        </div>
        <div><strong>{t('payment.name')}:</strong> {reservation.name}</div>
        <div><strong>{t('payment.email')}:</strong> {reservation.email}</div>
        <div><strong>{t('payment.phone')}:</strong> {reservation.phone}</div>
      </div>

      <button
        className="w-full bg-blue-600 text-white py-3 rounded hover:bg-blue-700 transition"
        onClick={() => void navigate('/')}
      >
        {t('cash.backHome')}
      </button>
    </div>
  );
};

export default CashPayment;
